import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Modal } from '~/components/ui/modal';
import { Button } from '~/components/ui/button';
import { TextField } from '~/components/form';

type ConfirmModalProps = {
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  /** Caller's own pending flag, OR-ed with the pending state of a promise returned by `onConfirm`. */
  busy?: boolean;
  /** When set, the confirm button stays disabled until the operator types exactly this (a server name, say). */
  confirmText?: string;
  testId?: string;
  /** Return the promise to keep both buttons disabled until it settles. */
  onConfirm: () => void | Promise<unknown>;
  onClose: () => void;
} & (
  | { variant?: 'default'; dangerNote?: never }
  | {
      variant: 'danger';
      /** Names what is lost, in the banner under the message. Required: a red confirmation must say why. */
      dangerNote: string;
    }
);

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmLabel,
  variant = 'default',
  dangerNote,
  busy = false,
  confirmText,
  testId = 'confirm-modal',
  onConfirm,
  onClose,
}) => {
  const titleId = `${testId}-title`;
  const [typed, setTyped] = useState('');
  const [pending, setPending] = useState(false);
  const working = busy || pending;
  const confirm = () => {
    if (working) return;
    const result = onConfirm();
    if (!(result instanceof Promise)) return;
    setPending(true);
    result.then(
      () => setPending(false),
      () => setPending(false),
    );
  };
  // Typed text never survives a close: reopening the same modal for another row starts empty.
  useEffect(() => {
    if (!isOpen) setTyped('');
  }, [isOpen]);
  const confirmBlocked = confirmText !== undefined && typed !== confirmText;
  const resolvedConfirmLabel = confirmLabel ?? (variant === 'danger' ? 'Delete' : 'Confirm');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      busy={working}
      size="small"
      title={title}
      titleId={titleId}
      testId={testId}
      footer={
        <>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onClose}
            disabled={working}
            testId={`${testId}-cancel`}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={confirm}
            loading={working}
            disabled={confirmBlocked}
            testId={`${testId}-confirm`}
          >
            {resolvedConfirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-gray-800 dark:text-white/90">{message}</p>
        {variant === 'danger' && (
          <div className="flex items-start gap-2 rounded-lg border border-error-200 bg-error-50 p-4 dark:border-error-800 dark:bg-error-900/20">
            <AlertTriangle
              className="mt-0.5 size-4 shrink-0 text-error-800 dark:text-error-200"
              aria-hidden="true"
            />
            <p className="text-sm font-medium text-error-800 dark:text-error-200">{dangerNote}</p>
          </div>
        )}
        {confirmText !== undefined && (
          <TextField
            id={`${testId}-confirm-text`}
            testId={`${testId}-confirm-text-input`}
            label={`Type ${confirmText} to confirm`}
            value={typed}
            onChange={setTyped}
            autoComplete="off"
          />
        )}
      </div>
    </Modal>
  );
};
